import clsx from 'clsx'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithoutRef, ElementType } from 'react'
import { themeClasses } from '../shared/themeClasses'

type HeadingProps = {
  level?: 1 | 2 | 3 | 4 | 5 | 6
} & ComponentPropsWithoutRef<'h1'>

const subheadingVariants = cva('', {
  variants: {
    variant: {
      default: 'font-semibold',
      dashboard:
        'dash-type-subheading font-sans text-lg/6 font-semibold tracking-tight',
    },
    tone: {
      primary: themeClasses.textPrimary,
      subtle: themeClasses.textSubtle,
    },
  },
  defaultVariants: {
    variant: 'default',
    tone: 'primary',
  },
})

type SubheadingProps = {
  level?: 2 | 3 | 4 | 5 | 6
} & ComponentPropsWithoutRef<'h2'> &
  VariantProps<typeof subheadingVariants>

type TextProps = ComponentPropsWithoutRef<'p'>

const strongTextVariants = cva('text-sm/6 font-semibold', {
  variants: {
    size: {
      sm: 'text-sm/6',
      base: 'text-base/6',
      lg: 'text-lg/6',
      xl: 'text-xl/6',
    },
    tone: {
      primary: themeClasses.textPrimary,
      secondary: themeClasses.textSecondary,
      subtle: themeClasses.textSubtle,
      muted: themeClasses.textMuted,
      faint: themeClasses.textFaint,
    },
  },
  defaultVariants: {
    size: 'sm',
    tone: 'primary',
  },
})

const mediumTextVariants = cva('text-sm/6 font-medium', {
  variants: {
    size: {
      xs: 'text-xs/5',
      sm: 'text-sm/6',
    },
    tone: {
      primary: themeClasses.textPrimary,
      secondary: themeClasses.textSecondary,
      subtle: themeClasses.textSubtle,
      muted: themeClasses.textMuted,
      faint: themeClasses.textFaint,
    },
  },
  defaultVariants: {
    size: 'sm',
    tone: 'primary',
  },
})

const eyebrowVariants = cva('text-xs/5 uppercase tracking-[0.04em]', {
  variants: {
    variant: {
      default: '',
      section: 'dash-type-section-label text-base/6 font-medium tracking-[0.04em]',
      mini: 'dash-type-mini-label text-base/6 font-medium tracking-[0.04em]',
      telemetry: 'dash-type-telemetry text-base/6 font-semibold tracking-[0.08em]',
    },
    tone: {
      primary: themeClasses.textPrimary,
      secondary: themeClasses.textSecondary,
      subtle: themeClasses.textSubtle,
      muted: themeClasses.textMuted,
      faint: themeClasses.textFaint,
    },
  },
  defaultVariants: {
    variant: 'default',
    tone: 'faint',
  },
})

const statusTextVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs/5',
      default: 'text-base/6',
    },
    tone: {
      danger: 'text-rose-700 dark:text-rose-400',
      success: 'text-emerald-700 dark:text-emerald-400',
    },
  },
  defaultVariants: {
    size: 'default',
    tone: 'success',
  },
})

type PolymorphicTextProps<T extends ElementType, Props = {}> = Props & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof Props | 'as'>

type StrongTextProps<T extends ElementType> = PolymorphicTextProps<
  T,
  VariantProps<typeof strongTextVariants>
>

type MediumTextProps<T extends ElementType> = PolymorphicTextProps<
  T,
  VariantProps<typeof mediumTextVariants>
>

type EyebrowProps<T extends ElementType> = PolymorphicTextProps<
  T,
  VariantProps<typeof eyebrowVariants>
>

type StatusTextProps<T extends ElementType> = PolymorphicTextProps<
  T,
  VariantProps<typeof statusTextVariants>
>

export function Heading({ className, level = 1, ...props }: HeadingProps) {
  const Element = `h${level}` as ElementType
  return <Element {...props} className={clsx('font-semibold', themeClasses.textPrimary, className)} />
}

export function Subheading({
  className,
  level = 2,
  tone,
  variant,
  ...props
}: SubheadingProps) {
  const Element = `h${level}` as ElementType
  return <Element {...props} className={clsx(subheadingVariants({ tone, variant }), className)} />
}

export function Text({ className, ...props }: TextProps) {
  return <p data-slot="text" {...props} className={clsx('text-base/6 sm:text-sm/6', themeClasses.textMuted, className)} />
}

export function StrongText<T extends ElementType = 'p'>({
  as,
  className,
  size,
  tone,
  ...props
}: StrongTextProps<T>) {
  const Element = (as ?? 'p') as ElementType

  return <Element {...props} className={clsx(strongTextVariants({ size, tone }), className)} />
}

export function MediumText<T extends ElementType = 'p'>({
  as,
  className,
  size,
  tone,
  ...props
}: MediumTextProps<T>) {
  const Element = (as ?? 'p') as ElementType

  return <Element {...props} className={clsx(mediumTextVariants({ size, tone }), className)} />
}

export function Eyebrow<T extends ElementType = 'p'>({
  as,
  className,
  tone,
  variant,
  ...props
}: EyebrowProps<T>) {
  const Element = (as ?? 'p') as ElementType

  return <Element {...props} className={clsx(eyebrowVariants({ tone, variant }), className)} />
}

export function StatusText<T extends ElementType = 'p'>({
  as,
  className,
  size,
  tone,
  ...props
}: StatusTextProps<T>) {
  const Element = (as ?? 'p') as ElementType

  return <Element {...props} className={clsx(statusTextVariants({ size, tone }), className)} />
}
